import React, { useState } from "react";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import { submitReportSection } from "../lib/apis/dailyReport";
import FileUploader from "../components/atoms/FileUploader";

const SECTIONS = ["AWS", "WhatsappBot", "Dashboard", "TV"] as const;
type SectionType = (typeof SECTIONS)[number];

const DailyReportForm: React.FC = () => {
  const [section, setSection] = useState<SectionType>("AWS");
  const [formData, setFormData] = useState<any>({});
  const [attachments, setAttachments] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await submitReportSection({
        sectionName: section,
        content: formData,
        attachments,
      });
      toast.success(res.message || "Section submitted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Error submitting section");
    } finally {
      setLoading(false);
    }
  };

  // ================= AWS =================
  const renderAWSFields = () => (
    <div className="grid grid-cols-2 gap-6">
      {[
        {
          label: "Stable",
          type: "select",
          key: "ECS_Stable",
          options: ["Select", "Yes", "No"],
        },
        { label: "CPU (%)", type: "input", key: "ECS_CPU" },
        { label: "Memory (%)", type: "input", key: "ECS_Memory" },
        {
          label: "RDS Stable",
          type: "select",
          key: "RDS_Stable",
          options: ["Select", "Yes", "No"],
        },
        { label: "RDS CPU (%)", type: "input", key: "RDS_CPU" },
        { label: "Burst Balance (%)", type: "input", key: "RDS_Burst_Balance" },
      ].map((f, idx) => (
        <div key={idx}>
          <label className="block text-sm font-semibold text-gray-700">
            {f.label}
          </label>
          {f.type === "input" ? (
            <input
              type="text"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={(e) => handleChange(f.key, e.target.value)}
            />
          ) : (
            <select
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={(e) => handleChange(f.key, e.target.value)}
            >
              {f.options?.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </select>
          )}
        </div>
      ))}
    </div>
  );

  // ================= WhatsappBot =================
  const renderWhatsappBotFields = () => (
    <div className="grid grid-cols-2 gap-6">
      {[
        {
          label: "Working",
          type: "select",
          key: "Working",
          options: ["Select", "Yes", "No"],
        },
        {
          label: "Booking Confirmation (x/5 working)",
          type: "input",
          key: "Booking_Confirmation",
        },
        {
          label: "General Instructions (x/5 working)",
          type: "input",
          key: "General_Instructions",
        },
        {
          label: "Patient Check-in (x/5 working)",
          type: "input",
          key: "Patient_Checkin",
        },
        {
          label: "Payments",
          type: "select",
          key: "Payments",
          options: ["Select", "Working", "Not Working"],
        },
        {
          label: "Reason Capture (x/5 working)",
          type: "input",
          key: "Reason_Capture",
        },
        {
          label: "Check Queue Status (x/10 working)",
          type: "input",
          key: "Queue_Status",
        },
        {
          label: "Doctor Check-in Message (x/5 working)",
          type: "input",
          key: "Doctor_Checkin",
        },
        {
          label: "Cancel Template (x/2 working)",
          type: "input",
          key: "Cancel_Template",
        },
      ].map((f, idx) => (
        <div key={idx} className={idx === 8 ? "col-span-2" : ""}>
          <label className="block text-sm font-semibold text-gray-700">
            {f.label}
          </label>
          {f.type === "input" ? (
            <input
              type="text"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={(e) => handleChange(f.key, e.target.value)}
            />
          ) : (
            <select
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={(e) => handleChange(f.key, e.target.value)}
            >
              {f.options?.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </select>
          )}
        </div>
      ))}
    </div>
  );

  // ================= Dashboard =================
  const renderDashboardFields = () => (
    <div className="grid grid-cols-2 gap-6">
      {[
        { label: "Login", key: "Dashboard_Login" },
        { label: "Loading", key: "Dashboard_Loading" },
        { label: "Booking Flow", key: "Dashboard_Booking_Flow", colSpan: true },
      ].map((f, idx) => (
        <div key={idx} className={f.colSpan ? "col-span-2" : ""}>
          <label className="block text-sm font-semibold text-gray-700">
            {f.label}
          </label>
          <select
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onChange={(e) => handleChange(f.key, e.target.value)}
          >
            <option>Select</option>
            <option>Working</option>
            <option>Not Working</option>
          </select>
        </div>
      ))}
    </div>
  );

  // ================= TV =================
  const renderTVFields = () => (
    <div className="grid grid-cols-1 gap-6">
      {[
        { label: "Login", key: "TV_Login" },
        { label: "Loading", key: "TV_Loading" },
        { label: "Booking Flow", key: "TV_Booking_Flow" },
      ].map((f, idx) => (
        <div key={idx}>
          <label className="block text-sm font-semibold text-gray-700">
            {f.label}
          </label>
          <select
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onChange={(e) => handleChange(f.key, e.target.value)}
          >
            <option>Select</option>
            <option>Working</option>
            <option>Not Working</option>
          </select>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex-1 p-8 bg-gray-50 min-h-screen">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-blue-600 mb-8 text-center">
          📊 Daily Testing Report
        </h1>

        {/* Section Selector */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Choose Section
          </label>
          <select
            value={section}
            onChange={(e) => setSection(e.target.value as SectionType)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {SECTIONS.map((sec) => (
              <option key={sec} value={sec}>
                {sec}
              </option>
            ))}
          </select>
        </div>

        {/* Section Fields */}
        {section === "AWS" && renderAWSFields()}
        {section === "WhatsappBot" && renderWhatsappBotFields()}
        {section === "Dashboard" && renderDashboardFields()}
        {section === "TV" && renderTVFields()}

        {/* File Upload */}
        <div className="mt-8">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Upload Images
          </label>
          <FileUploader
            folder={`report_sections/${section}`}
            onUploadComplete={setAttachments}
          />
        </div>

        {/* Submit */}
        <div className="mt-10 text-center">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-8 py-3 rounded-lg font-semibold text-white transition ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <ClipLoader size={20} color="#fff" className="mr-2" /> Saving...
              </span>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyReportForm;
